import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Alert, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import AsyncFormDialog from '../../lib/AsyncFormDialog';
import { useTagUniverse } from '../../lib/useTagUniverse';
import TagInput from './TagInput';

const SHORT_INFO_TEMPLATE =
    'A one-sentence description: <what the dataset contains>, <how it was collected>, <intended use>.';

const LONG_INFO_TEMPLATE = `## Overview
<2-4 sentences describing the dataset, its purpose and origin>

## Data sources
- <Sensor / system / origin 1>
- <Sensor / system / origin 2>

## Schema / format
<Brief description of file formats, columns, units>

## Collection period
<Start date – end date, sampling rate>

## Caveats
<Any known gaps, biases or constraints>`;

const CATEGORIES = [
    { value: 'metric', label: 'Metric' },
    { value: 'log', label: 'Log' }
];

const ACCESS_TYPES = [
    { value: 'closed', label: 'Restricted access' },
    { value: 'open', label: 'Open access' }
];

// TagInput already enforces "trimmed, non-empty, case-insensitively
// unique" at commit time, so by the time tagsArray reaches submit it is
// invariant-by-construction. We keep a defensive duplicate check below
// (defence in depth) but it should never fire in practice.

/**
 * Reusable Create Dataset dialog.
 *
 * `short_info` and `long_info` are required. Templates are offered via an
 * "Insert template" link on each field's helper text instead of being
 * pre-filled into the input — that way the user sees an empty form, not a
 * pre-populated form they need to clear (anti-pattern, DES3).
 *
 * Built on AsyncFormDialog: submit/error/lock/close plumbing is shared.
 *
 * Props:
 * - open: bool
 * - onSubmit(body): async — pass the create body straight to the mutation
 * - onSuccess?: () => void — fired on successful creation (e.g. snackbar)
 * - onClose(): void
 */
export default function CreateDatasetDialog({ open, onSubmit, onSuccess, onClose }) {
    const [name, setName] = useState('');
    const [category, setCategory] = useState('metric');
    const [shortInfo, setShortInfo] = useState('');
    const [longInfo, setLongInfo] = useState('');
    const [accessType, setAccessType] = useState('closed');
    const [tagsArray, setTagsArray] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const nameRef = useRef(null);
    // Full-screen on phones (DES25). Same reasoning as MqttSetupDialog.
    const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

    // Existing-tag suggestions surfaced from the catalog. Empty until
    // the datasets query resolves; the field stays usable (freeSolo
    // typing) regardless.
    const tagSuggestions = useTagUniverse();

    const validate = () => {
        const errs = {};
        if (!name || name.trim().length < 2) {
            errs.name = 'Name must be at least 2 characters';
        }
        if (!shortInfo || shortInfo.trim().length < 10) {
            errs.shortInfo = 'A one-sentence description is required (min. 10 characters)';
        }
        if (!longInfo || longInfo.trim().length < 20) {
            errs.longInfo = 'A longer description is required (min. 20 characters)';
        }
        // Defence in depth: TagInput dedups, but if a future caller
        // bypasses it we still want a coherent error rather than a
        // silent backend rejection.
        const lowered = tagsArray.map(t => t.toLowerCase());
        if (new Set(lowered).size !== lowered.length) {
            errs.tags = 'Tags must be unique (case-insensitive)';
        }
        setFieldErrors(errs);
        return { ok: Object.keys(errs).length === 0 };
    };

    const reset = () => {
        setName('');
        setCategory('metric');
        setShortInfo('');
        setLongInfo('');
        setAccessType('closed');
        setTagsArray([]);
        setFieldErrors({});
    };

    // AsyncFormDialog calls onSubmit with no args; close it by going via
    // its onSuccess / onClose hooks.
    const submitNow = async () => {
        const v = validate();
        if (!v.ok) {
            // Throw an error AsyncFormDialog will swallow into the Alert,
            // but we'd rather not do that — instead, just bail. The
            // primary button will go disabled while we show field errors.
            const ve = new Error('Please fix the highlighted fields and try again.');
            ve.data = 'Please fix the highlighted fields and try again.';
            throw ve;
        }
        await onSubmit({
            name: name.trim(),
            category,
            short_info: shortInfo,
            long_info: longInfo,
            access_type: accessType,
            tags: tagsArray
        });
    };

    return (
        <AsyncFormDialog
            open={open}
            onClose={() => {
                reset();
                onClose();
            }}
            title="Create a new dataset"
            subtitle="Owner and organization are taken from your account. You can update most metadata later from the Overview tab."
            submitLabel="Create dataset"
            onSubmit={submitNow}
            onSuccess={() => {
                reset();
                if (typeof onSuccess === 'function') onSuccess();
            }}
            dialogProps={{ fullWidth: true, maxWidth: 'sm', fullScreen }}
            >
            <TextField
                label="Dataset name"
                fullWidth required margin="dense" size="small"
                value={name}
                inputRef={nameRef}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || 'Short, recognisable name. Required.'}
            />
            <FormControl fullWidth margin="dense" size="small">
                <InputLabel id="create-dataset-category-label">Category</InputLabel>
                <Select
                    labelId="create-dataset-category-label"
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                >
                    {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
            </FormControl>
            <TextField
                label="Short description"
                fullWidth required margin="dense" size="small"
                multiline rows={2}
                value={shortInfo}
                placeholder={SHORT_INFO_TEMPLATE}
                onChange={(e) => setShortInfo(e.target.value)}
                error={!!fieldErrors.shortInfo}
                helperText={
                    fieldErrors.shortInfo || (
                        <>
                            One sentence shown in the dataset catalog.{' '}
                            <Button
                                size="small"
                                variant="text"
                                sx={{ p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}
                                onClick={() => setShortInfo(SHORT_INFO_TEMPLATE)}
                            >
                                Insert template
                            </Button>
                        </>
                    )
                }
            />
            <TextField
                label="Long description"
                fullWidth required margin="dense" size="small"
                multiline rows={10}
                value={longInfo}
                placeholder={LONG_INFO_TEMPLATE}
                onChange={(e) => setLongInfo(e.target.value)}
                error={!!fieldErrors.longInfo}
                helperText={
                    fieldErrors.longInfo || (
                        <>
                            Detailed description shown in the Overview tab.{' '}
                            <Button
                                size="small"
                                variant="text"
                                sx={{ p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}
                                onClick={() => setLongInfo(LONG_INFO_TEMPLATE)}
                            >
                                Insert template
                            </Button>
                        </>
                    )
                }
            />
            <FormControl fullWidth margin="dense" size="small">
                <InputLabel id="create-dataset-access-type-label">Access type</InputLabel>
                <Select
                    labelId="create-dataset-access-type-label"
                    label="Access type"
                    value={accessType}
                    onChange={(e) => setAccessType(e.target.value)}
                >
                    {ACCESS_TYPES.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                </Select>
            </FormControl>
            {accessType === 'open' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    With open access, anyone logged in can view the data in this dataset.
                </Alert>
            )}
            <TagInput
                label="Tags"
                value={tagsArray}
                onChange={setTagsArray}
                suggestions={tagSuggestions}
                error={!!fieldErrors.tags}
                helperText={fieldErrors.tags
                    || 'Optional. Pick from existing tags or type new ones; press Enter or comma to commit.'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Required fields are marked with *
            </Typography>
        </AsyncFormDialog>
    );
}

CreateDatasetDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired
};

// Exported for tests / reuse
export const __TEMPLATES__ = { SHORT_INFO_TEMPLATE, LONG_INFO_TEMPLATE };
